import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  Container,
  Box,
  Typography,
  useTheme,
  Grid,
} from "@mui/material";
import { styled } from "@mui/system";
import { School, Shield, Bolt } from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  APP_NAME,
  HORNET_TRIBUNE_FEATURE,
  PRIVACY_POLICY,
  NOTION_WHY_WE_STARTED,
  NOTION_TERMS_OF_SERVICE,
  CONTACT_EMAIL,
} from "../../constants/constants";
import LogoDot from "../../components/LogoDot/LogoDot";

// ----- Motion helpers -----
const MotionBox = motion(Box);
const MotionGrid = motion(Grid);

// ----- Styled Components -----

// Full-page wrapper so header + hero share same gradient
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background:
    "radial-gradient(circle at top left, #58A6FF22, transparent 55%), #0F172A",
  color: "#F9FAFB",
}));

const HeaderNav = styled("nav")(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  gap: theme.spacing(1.5),
  alignItems: "center",
}));

const HeaderWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 0),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(3, 4, 0),
  },
  display: "flex",
  alignItems: "center",
  zIndex: 1,
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: "1.7rem",
  fontWeight: 800,
  fontFamily: "'Chalkboard SE', 'Comic Sans MS', system-ui",
  letterSpacing: "-0.03em",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.8),
}));

const MainContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(4, 2, 6),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4, 4, 8),
  },
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  boxSizing: "border-box",
}));

const HeroInner = styled(Container)(({ theme }) => ({
  maxWidth: "1120px",
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
  },
}));

const Headline = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  lineHeight: 1.1,
  letterSpacing: "-0.03em",
  marginBottom: theme.spacing(2),
  fontFamily: "'Chalkboard SE', 'Comic Sans MS', system-ui, -apple-system",
  fontSize: "2.2rem",
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.8rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3.2rem",
  },
}));

const SubText = styled(Typography)(({ theme }) => ({
  color: "rgba(249, 250, 251, 0.8)",
  maxWidth: "520px",
  marginBottom: theme.spacing(3),
  textAlign: "left",

  // On mobile / small screens: center the block + text
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
    marginLeft: "auto",
    marginRight: "auto",
  },

  [theme.breakpoints.up("md")]: {
    fontSize: "1.05rem",
  },
}));

const EmojiRow = styled("p")(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: "1.2rem",
}));

const CTAWrapper = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const TrustBadge = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  fontSize: "0.85rem",
  opacity: 0.85,
}));

const MediaContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginTop: theme.spacing(4),
  [theme.breakpoints.up("md")]: {
    marginTop: 0,
  },
}));

// ---- Demo "How it works" mock UI used on hero right side ----

const DemoCard = styled(Box)(({ theme }) => ({
  borderRadius: 24,
  padding: theme.spacing(3),
  background:
    "radial-gradient(circle at top left, rgba(15,23,42,0.9), rgba(15,23,42,0.98))",
  border: "1px solid rgba(148,163,184,0.5)",
  boxShadow: "0 22px 60px rgba(15,23,42,0.9)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: 480,
}));

const DemoBorder = styled(Box)(({ theme }) => ({
  borderRadius: 18,
  border: "1px dashed rgba(148,163,184,0.7)",
  padding: theme.spacing(2.5),
  fontFamily: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, monospace',
  color: "#E5E7EB",
}));

const DemoHeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2.5),
}));

const DemoTag = styled(Box)(({ theme }) => ({
  borderRadius: 999,
  padding: theme.spacing(0.5, 1.5),
  backgroundColor: "rgba(15,23,42,0.9)",
  border: "1px solid rgba(248, 250, 252, 0.15)",
  fontSize: 12,
}));

const DemoLiveDot = styled(motion.span)(() => ({
  width: 9,
  height: 9,
  borderRadius: "999px",
  background: "#58A6FF",
  display: "inline-block",
  position: "relative",
  boxShadow: "0 0 0 0 rgba(88, 166, 255, 0.5)",
  marginRight: 6,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: -4,
    borderRadius: "999px",
    background:
      "radial-gradient(circle, rgba(88,166,255,0.4), transparent 60%)",
    opacity: 0.85,
    zIndex: -1,
  },
}));

const DemoVideoRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2.5),
}));

const DemoTile = styled(Box)(({ theme }) => ({
  borderRadius: 16,
  border: "1px solid rgba(148,163,184,0.6)",
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  minHeight: 110,
  background:
    "linear-gradient(135deg, rgba(30,64,175,0.2), rgba(15,23,42,0.95))",
}));

const DemoAvatar = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 999,
  background:
    "radial-gradient(circle at 30% 30%, #FDE68A, #F97316, #EA580C)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 20,
}));

const DemoTileLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "rgba(209, 213, 219, 0.9)",
}));

const DemoTileName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
}));

const DemoControls = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  display: "flex",
  justifyContent: "center",
}));

const DemoControlsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2.5),
  alignItems: "flex-start",
}));

const DemoControl = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontSize: 11,
  color: "rgba(156, 163, 175, 0.96)",
}));

const DemoControlButton = styled(Box)(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: "999px",
  backgroundColor: "#3B82F6",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 6px 16px rgba(15,23,42,0.7)",
}));

const DemoControlIcon = styled("span")(() => ({
  fontSize: 14,
  color: "#E5E7EB",
}));

// benefits
const BenefitsWrapper = styled(Container)(({ theme }) => ({
  maxWidth: "1120px",
  marginTop: theme.spacing(8),
}));

const BenefitCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: "rgba(15, 23, 42, 0.85)",
  border: "1px solid rgba(148, 163, 184, 0.35)",
}));

// ----- Components -----

const WelcomeSection = () => {
  return (
    <HeroInner>
      <Grid container spacing={4} alignItems="center">
        {/* Left: headline + copy + CTAs */}
        <MotionGrid
          item
          xs={12}
          md={6}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Headline variant="h1">
            Meet new students in seconds, not semesters.
          </Headline>
          <SubText variant="body1">
            Instant video chats with real students on your campus (and nearby
            schools). No swiping, no follower counts—just real conversations
            with people you might actually vibe with.
            <EmojiRow>🤝 🎧 📚 🎉 💬</EmojiRow>
          </SubText>

          <CTAWrapper
            container
            spacing={2}
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Grid item xs={12} sm={6}>
              <RouterLink
                to="/login"
                style={{
                  textDecoration: "none",
                  display: "block",
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#58A6FF",
                    color: "#0D1117",
                    fontWeight: 700,
                    py: 1.2,
                    borderRadius: "999px",
                    "&:hover": {
                      backgroundColor: "#7BB7FF",
                    },
                  }}
                >
                  Start Chat
                </Button>
              </RouterLink>
            </Grid>
            <Grid item xs={12} sm={6}>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                style={{
                  textDecoration: "none",
                  display: "block",
                  width: "100%",
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: "#58A6FF",
                    color: "#E5E7EB",
                    fontWeight: 600,
                    py: 1.2,
                    borderRadius: "999px",
                    "&:hover": {
                      borderColor: "#7BB7FF",
                      backgroundColor: "rgba(88, 166, 255, 0.08)",
                    },
                  }}
                >
                  Talk to the team
                </Button>
              </a>
            </Grid>
          </CTAWrapper>

          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "rgba(249, 250, 251, 0.55)",
                fontStyle: "italic",
                mb: 0.5,
              }}
            >
              It&apos;s like bumping into someone in the library… minus the awkward
              eye contact.
            </Typography>

            <TrustBadge variant="caption">
              Featured in{" "}
              <a
                href={HORNET_TRIBUNE_FEATURE}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#93C5FD",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                The Hornet Tribune
              </a>
            </TrustBadge>
          </MotionBox>
        </MotionGrid>

        {/* Right: How-it-works demo card */}
        <MotionGrid
          item
          xs={12}
          md={6}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <MediaContainer>
            <DemoCard>
                <DemoHeaderRow>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13, color: "#F9A8D4" }}>
                      [you]
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "rgba(156,163,175,0.95)",
                      }}
                    >
                      You&apos;re paired with another student in seconds.
                    </Typography>
                  </Box>

                  <DemoTag>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <DemoLiveDot
                        animate={{
                          scale: [1, 1.08, 1],
                          boxShadow: [
                            "0 0 0 0 rgba(88,166,255,0.5)",
                            "0 0 0 6px rgba(88,166,255,0.0)",
                            "0 0 0 0 rgba(88,166,255,0.0)",
                          ],
                        }}
                        transition={{
                          duration: 1.6,
                          repeat: Infinity,
                          repeatType: "loop",
                          ease: "easeOut",
                        }}
                      />
                      <span style={{ fontSize: 11 }}>Live</span>
                    </Box>
                  </DemoTag>
                </DemoHeaderRow>

                <DemoVideoRow>
                  <DemoTile>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <DemoAvatar>😄</DemoAvatar>
                      <Box>
                        <DemoTileLabel>You</DemoTileLabel>
                        <DemoTileName>Camera on/off, vibes up!</DemoTileName>
                      </Box>
                    </Box>
                  </DemoTile>

                  <DemoTile>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <DemoAvatar>🤝</DemoAvatar>
                      <Box>
                        <DemoTileLabel>New match</DemoTileLabel>
                        <DemoTileName>Other college student</DemoTileName>
                      </Box>
                    </Box>
                  </DemoTile>
                </DemoVideoRow>

                <DemoControls>
                  <DemoControlsRow>
                    <DemoControl>
                      <DemoControlButton>
                        <DemoControlIcon>▶</DemoControlIcon>
                      </DemoControlButton>
                      <span>start</span>
                    </DemoControl>

                    <DemoControl>
                      <DemoControlButton>
                        <DemoControlIcon>⏭</DemoControlIcon>
                      </DemoControlButton>
                      <span>next</span>
                    </DemoControl>

                    <DemoControl>
                      <DemoControlButton>
                        <DemoControlIcon>■</DemoControlIcon>
                      </DemoControlButton>
                      <span>stop</span>
                    </DemoControl>

                    <DemoControl>
                      <DemoControlButton>
                        <DemoControlIcon>💬</DemoControlIcon>
                      </DemoControlButton>
                      <span>Text-Chat</span>
                    </DemoControl>

                    <DemoControl>
                      <DemoControlButton>
                        <DemoControlIcon>👍</DemoControlIcon>
                      </DemoControlButton>
                      <span>like them!</span>
                    </DemoControl>
                  </DemoControlsRow>
                </DemoControls>
            </DemoCard>
          </MediaContainer>
        </MotionGrid>
      </Grid>
    </HeroInner>
  );
};

const BenefitsSection = () => {
  return (
    <BenefitsWrapper>
      <Grid container spacing={3}>
        {[
          {
            icon: <Bolt sx={{ mr: 1, fontSize: 22 }} />,
            title: "Zero-awkward icebreakers",
            body: `Tap "Start Chat" and you're instantly matched with another student. No bios to overthink, no swiping marathons.`,
            delay: 0.1,
          },
          {
            icon: <School sx={{ mr: 1, fontSize: 22 }} />,
            title: "Built for campus life",
            body: "Meet people in your dorms, classes, and nearby schools. Study buddies, club friends, or just someone to rant with.",
            delay: 0.2,
          },
          {
            icon: <Shield sx={{ mr: 1, fontSize: 22 }} />,
            title: "Safe by design",
            body: "Report, block, and vibe only with people you feel comfortable with. We keep it friendly and campus-focused.",
            delay: 0.3,
          },
        ].map((card) => (
          <Grid item xs={12} md={4} key={card.title}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: card.delay }}
            >
              <BenefitCard>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  {card.icon}
                  <Typography variant="subtitle1" fontWeight={700}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(249,250,251,0.8)" }}
                >
                  {card.body}
                </Typography>
              </BenefitCard>
            </MotionBox>
          </Grid>
        ))}
      </Grid>
    </BenefitsWrapper>
  );
};

const handleButtonClick = (url) => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

// ----- Main Landing Component -----

export default function Landing() {
  const theme = useTheme();

  return (
    <>
      <PageWrapper>
        {/* Header inside gradient */}
        <HeaderWrapper>
          <LogoText
            component={RouterLink}
            to="/"
            style={{ color: "#F9FAFB", textDecoration: "none" }}
          >
            <LogoDot
              animate={{
                scale: [1, 1.08, 1],
                boxShadow: [
                  "0 0 0 0 rgba(88, 166, 255, 0.45)",
                  "0 0 0 6px rgba(88, 166, 255, 0.0)",
                  "0 0 0 0 rgba(88, 166, 255, 0.0)",
                ],
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                repeatType: "loop",
                ease: "easeOut",
              }}
            />
            {APP_NAME}
          </LogoText>

          <HeaderNav>
            <Button
              variant="outlined"
              sx={{
                borderColor: "rgba(148,163,184,0.6)",
                color: "#E5E7EB",
                fontWeight: 500,
                borderRadius: "999px",
                textTransform: "none",
                fontSize: "0.85rem",
                px: 2.4,
                py: 0.6,
                "&:hover": {
                  borderColor: "#7BB7FF",
                  backgroundColor: "rgba(88, 166, 255, 0.12)",
                },
              }}
              onClick={() => handleButtonClick(NOTION_WHY_WE_STARTED)}
            >
              Our story
            </Button>
          </HeaderNav>
        </HeaderWrapper>

        {/* Hero + Benefits */}
        <MainContainer>
          <WelcomeSection />
          <BenefitsSection />
        </MainContainer>
      </PageWrapper>

      {/* Footer */}
      <FooterComponent />
    </>
  );
}

// ----- Footer -----

function FooterComponent() {
  const theme = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 3,
        py: 1.5,
        width: "100%",
        borderTop: 1,
        borderColor: "divider",
        alignItems: "center",
        px: 2,
        mt: "auto",
        backgroundColor:
          theme.palette.mode === "dark"
            ? "#020617"
            : theme.palette.background.paper,
        [theme.breakpoints.up("sm")]: {
          flexDirection: "row",
          py: 2,
          px: 4,
        },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          fontSize: "0.9rem",
          textAlign: "center",
          [theme.breakpoints.up("sm")]: { textAlign: "left" },
        }}
      >
        © {new Date().getFullYear()} VibeU
      </Typography>

      <a
        href={HORNET_TRIBUNE_FEATURE}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: "0.85rem",
            px: 0.75,
            py: 0.25,
            borderRadius: "6px",
            color: "text.secondary",
            textDecoration: "none",
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.08)",
              color: "text.primary",
            },
          }}
        >
          Media
        </Typography>
      </a>

      <a
        href={NOTION_TERMS_OF_SERVICE}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <Typography
          component="a"
          href={NOTION_TERMS_OF_SERVICE}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{
            fontSize: "0.85rem",
            px: 0.75,
            py: 0.25,
            borderRadius: "6px",
            color: "text.secondary",
            textDecoration: "none",
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.08)",
              color: "text.primary",
            },
          }}
        >
          Terms of Service
        </Typography>
      </a>

      <a
        href={PRIVACY_POLICY}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: "0.85rem",
            px: 0.75,
            py: 0.25,
            borderRadius: "6px",
            color: "text.secondary",
            textDecoration: "none",
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.08)",
              color: "text.primary",
            },
          }}
        >
          Privacy Policy
        </Typography>
      </a>
    </Box>
  );
}
